import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

import { token, type DeltaDirection } from '../tokens';
import { mergeStyle, type StyleWithVars } from '../internal/style';
import styles from './MetricTile.module.css';

export interface MetricTileProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Uppercase label. Short: this is a strip, not a paragraph. */
  label: ReactNode;
  value: ReactNode;
  /** Rendered small beside the figure: `days`, `%`, `FTE`. */
  unit?: ReactNode;
  caption?: ReactNode;
  /** Signed change with its direction. The word is required, the colour is not. */
  delta?: { direction: DeltaDirection; label: ReactNode };
  /**
   * The one licensed `display-01` figure — the Compare cycle-time delta on the
   * black executive band (TOKENS.md §8.2). Anywhere else it is a defect.
   */
  figure?: boolean;
  /** Renders on the black band: inverse text and the inverse focus contract. */
  onInverse?: boolean;
}

const DELTA_TOKEN = {
  improve: token.domain.delta.improve,
  regress: token.domain.delta.regress,
  neutral: token.domain.delta.neutral,
} as const;

export const MetricTile = forwardRef<HTMLDivElement, MetricTileProps>(function MetricTile(
  { label, value, unit, caption, delta, figure = false, onInverse = false, className, style, ...rest },
  ref,
) {
  const vars: StyleWithVars = {
    '--dpem-metric-delta': delta === undefined ? token.text.secondary : DELTA_TOKEN[delta.direction],
  };

  return (
    <div
      {...rest}
      ref={ref}
      data-focus-scope={onInverse ? 'inverse' : undefined}
      className={clsx(
        styles.tile,
        figure && styles.figure,
        onInverse && styles.inverse,
        className,
      )}
      style={mergeStyle(vars, style)}
    >
      <p className={styles.label}>{label}</p>
      <p className={styles.valueRow}>
        <span className={styles.value}>{value}</span>
        {unit !== undefined && <span className={styles.unit}>{unit}</span>}
      </p>
      {delta !== undefined && (
        <p className={styles.delta} data-direction={delta.direction}>
          {delta.label}
        </p>
      )}
      {caption !== undefined && <p className={styles.caption}>{caption}</p>}
    </div>
  );
});
